import { useMemo } from "react";
import { formatToCurrency } from "utils/currency";
import { Pagination } from "shared/components/Pagination";

import { statementActivity } from "../types";
import { PaginatedResponse } from "shared/types";
import { TransactionType } from "types/billing";
import { Table } from "shared/app/Table";

import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { cx } from "shared/utils/classnames";
import { freeItems } from "views/statements-id/utils";

interface TableData {
  description: string;
  product?: string;
  person_name: string;
  email: string;
  property?: {
    address: string;
    name: string;
  };
  unit?: string;
  fee?: {
    cents: number;
    currency: string;
  };
}

interface BillingDetailTableProps {
  statementActivities: PaginatedResponse<statementActivity>;
  setPage: (page: number | ((prevPage: number) => number)) => void;
  type: TransactionType;
  isLoading: boolean;
}

export const BillingDetailTable = ({
  statementActivities,
  setPage,
  isLoading,
  type
}: BillingDetailTableProps) => {
  const mapping = useMemo(
    () =>
      statementActivities.items.reduce<TableData[]>((acc, item) => {
        if (
          type === TransactionType.Payout &&
          item.metadata.items &&
          item.metadata.items.length > 0
        ) {
          const res: TableData[] = [];

          item.metadata.items.forEach((i) => {
            if (freeItems.includes(i.name)) return;
            else
              res.push({
                description: item.description,
                product: item.metadata.product,
                person_name: item.metadata.name,
                email: item.metadata.email,
                property: {
                  address: item.metadata.one_line_address,
                  name: item.metadata.property_name
                },
                unit: item.metadata.unit,
                ...i
              });
          });

          return res;
        } else
          acc.push({
            description: item.description,
            product: item.metadata.product,
            person_name: item.metadata.name,
            property: {
              address: item.metadata.one_line_address,
              name: item.metadata.property_name
            },
            unit: item.metadata.unit,
            email: item.metadata.email,
            fee: {
              cents: item.amount.cents,
              currency: item.amount.currency
            }
          });

        return acc;
      }, []),
    [statementActivities, type]
  );

  const columns = useMemo(() => {
    return [
      {
        id: "product fixed_width",
        accessorKey: "product",
        header: "Product",
        size: 131,
        cell: (data) => (
          <p className="text-sm text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "description",
        header: "Item"
      },
      {
        accessorKey: "person_name",
        header: "Name"
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (data) => (
          <p className="text-sm max-w-[150px] truncate">{data.getValue()}</p>
        )
      },
      {
        accessorKey: "property",
        header: "Property",
        cell: (data) => {
          const property = data.getValue();
          return (
            <>
              <p className="text-sm max-w-[150px] truncate text-content-1000">
                {property.name}
              </p>
              <p className="text-xs text-content-400 max-w-[150px] truncate mt-1">
                {property.address}
              </p>
            </>
          );
        }
      },
      {
        id: "unit fixed_width",
        accessorKey: "unit",
        header: "Unit",
        size: 156,
        cell: (data) => (
          <p className="max-w-[150px] truncate">{data.getValue()}</p>
        )
      },
      {
        id: "amount fixed_width right-align",
        accessorKey: "fee.cents",
        header: "Amount",
        size: 137,
        cell: (info) => {
          return (
            <p
              className={cx(
                "font-medium text-sm text-end",
                info.getValue() >= 0 ? "text-success-700" : "text-danger-600"
              )}
            >
              {`${info.getValue() > 0 ? "+" : ""}${formatToCurrency({
                cents: info.getValue(),
                currency: "USD"
              })}`}
            </p>
          );
        }
      }
    ];
  }, []);

  const table = useReactTable({
    data: mapping,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <div>
      <Table table={table} isLoading={isLoading} />
      <div className="ml-auto mt-10 flex shrink-0 w-full max-w-[calc(100vw-106px)]">
        <Pagination
          total={statementActivities.page_count}
          current={statementActivities.current_page}
          onChange={setPage}
        />
      </div>
    </div>
  );
};
